/**
 * Commercial quotes. The API's OpenAPI now carries the quote schemas, so the shapes
 * come straight from the generated contract — this module only aliases them (matching
 * `contract.ts`'s single-re-export convention) and holds the request functions.
 *
 * Two asymmetries the generated types encode, and the UI depends on:
 *   • `CustomerQuote` has `recipientCount` instead of `recipients`, and no `notes` at
 *     all — the recipient list and the operator's internal notes never leave the
 *     support console.
 *   • `CustomerQuoteInvoice.dueAt` and `.billingInterval` are nullable, because the
 *     Stripe invoice is created asynchronously; every render path handles that.
 */
import { request } from "./client";
import type { components } from "./contract";

type Schemas = components["schemas"];

export type QuoteStatus = Schemas["AdminQuoteStatus"];
export type QuoteOption = Schemas["AdminQuoteOption"];
export type QuoteDelivery = Schemas["AdminQuoteDelivery"];
/** Per-recipient outcome of the portal-link email fan-out. */
export type QuoteDeliveryStatus = QuoteDelivery["status"];

/** Support-side view. Carries the full recipient list — admin console only. */
export type AdminQuote = Schemas["AdminQuote"];
export type AdminQuoteWithActivity = Schemas["AdminQuoteWithActivity"];
/**
 * Customer-side view. The recipient list is deliberately absent: a procurement
 * contact must never be able to enumerate who else received the quote.
 */
export type CustomerQuote = Schemas["CustomerQuote"];

export type AdminQuoteOptionInput = Schemas["AdminQuoteOptionCreateRequest"];
export type AdminQuoteCreateRequest = Schemas["AdminQuoteCreateRequest"];
export type AdminQuoteCreation = Schemas["AdminQuoteCreation"];

/** The Stripe invoice materialized by accepting exactly one option. */
export type QuoteInvoice = Schemas["CustomerQuoteInvoice"];
export type QuoteAcceptance = Schemas["CustomerQuoteAcceptance"];
export type QuoteAcceptRequest = Schemas["CustomerQuoteAcceptanceRequest"];

// ------------------------------------------------------------------ support side

export async function listAdminQuotes(customerId: string, signal?: AbortSignal): Promise<AdminQuoteWithActivity[]> {
  const res = await request<AdminQuoteWithActivity[]>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/quotes`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function createAdminQuote(
  customerId: string,
  body: AdminQuoteCreateRequest,
  idempotencyKey: string
): Promise<AdminQuoteCreation> {
  const res = await request<AdminQuoteCreation>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/quotes`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}

export async function resendAdminQuote(
  customerId: string,
  quoteId: string,
  idempotencyKey: string
): Promise<AdminQuoteCreation> {
  const res = await request<AdminQuoteCreation>(
    `/v1/admin/entitlement-customers/${encodeURIComponent(customerId)}/quotes/${encodeURIComponent(quoteId)}/resend`,
    { method: "POST", idempotencyKey }
  );
  return res.data;
}

// ----------------------------------------------------------------- customer side

/**
 * Authorization is server-side and recipient-based: a signed-in organization member
 * only sees the quotes their own verified address was named on. The route guard in
 * front of these screens is convenience, never the security boundary.
 */
export async function listOrganizationQuotes(orgId: string, signal?: AbortSignal): Promise<CustomerQuote[]> {
  const res = await request<CustomerQuote[]>(
    `/v1/orgs/${encodeURIComponent(orgId)}/quotes`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function getOrganizationQuote(
  orgId: string,
  quoteId: string,
  signal?: AbortSignal
): Promise<CustomerQuote> {
  const res = await request<CustomerQuote>(
    `/v1/orgs/${encodeURIComponent(orgId)}/quotes/${encodeURIComponent(quoteId)}`,
    signal ? { signal } : {}
  );
  return res.data;
}

export async function acceptOrganizationQuote(
  orgId: string,
  quoteId: string,
  body: QuoteAcceptRequest,
  idempotencyKey: string
): Promise<QuoteAcceptance> {
  const res = await request<QuoteAcceptance>(
    `/v1/orgs/${encodeURIComponent(orgId)}/quotes/${encodeURIComponent(quoteId)}/accept`,
    { method: "POST", body, idempotencyKey }
  );
  return res.data;
}
